<script module>
  // Sheets open while others close — the delete sheet replaces the Me sheet — so the page lock is
  // counted across all of them: the first to open locks the page, the last to close unlocks it.
  let locks = 0;
  let unlockedOverflow = "";
  // What opened each open sheet. A sheet opened from inside another takes over that one's opener,
  // so focus finds its way back to the page however many sheets were passed through.
  /** @type {WeakMap<Element, HTMLElement | null>} */
  const openers = new WeakMap();
</script>

<script>
  /**
   * The frame every bottom sheet shares: the backdrop, the panel, the grab handle, and the gestures
   * a phone user expects of them.
   *
   * - **Pull down to close.** A downward drag with the sheet scrolled to its top follows the finger,
   *   and closes the sheet if it travels far enough or is flicked. A shorter pull springs back.
   *   Anything else is an ordinary scroll of the sheet's own content.
   * - **The page underneath stays put.** Without this, a drag that the sheet cannot use — it is
   *   already at its top, or too short to scroll — scrolls the list behind it instead.
   *
   * Fields keep their own gestures: a drag that starts in an input or a textarea is never taken as
   * a pull, so selecting text or scrolling a long note still works.
   *
   * It rises from the bottom edge as it opens and sinks back as it closes, the backdrop fading with
   * it, rather than appearing at once. Closed by a pull, it sinks on from where the finger left it.
   * Reduce Motion makes both instant.
   *
   * Focus goes into the sheet as it opens and back to what opened it as it closes.
   *
   * **It stays above the keyboard.** iOS does not shrink the page for the keyboard: it slides the
   * keyboard over it and scrolls the view to keep the field in sight, which carried a sheet pinned
   * to the page's bottom up past the top of the screen. So the sheet follows the visual viewport —
   * the part of the screen the keyboard leaves — sitting on the keyboard and never taller than
   * what is left, its content scrolling inside it instead.
   *
   * Callers render it inside their own `{#if}` and keep their own Escape handling.
   */
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import { cubicIn, cubicOut } from "svelte/easing";
  import { getTranslations } from "$lib/utils/locale.svelte.js";

  /** @type {{ label: string, onClose: () => void, children: import("svelte").Snippet }} */
  let { label, onClose, children } = $props();

  const t = $derived(getTranslations());

  // How far a pull must travel, or how fast it must be flicked (px/ms), to close the sheet.
  const CLOSE_DISTANCE = 120;
  const CLOSE_VELOCITY = 0.6;

  /** @type {HTMLDivElement | undefined} */
  let panel = $state();
  let dragY = $state(0);
  let dragging = $state(false);
  /** @type {{ y: number, at: number } | null} */
  let start = null;

  // The visual viewport, followed while the sheet is up: how far the keyboard covers the page's
  // bottom, and how much screen is left above it. Zoomed in, it is left alone.
  let keyboard = $state(0);
  let visible = $state(0);
  $effect(() => {
    const view = window.visualViewport;
    if (!view) return;
    const follow = () => {
      const zoomed = Math.abs(view.scale - 1) > 0.01;
      keyboard = zoomed ? 0 : Math.max(0, Math.round(window.innerHeight - view.height - view.offsetTop));
      visible = zoomed ? 0 : Math.round(view.height);
    };
    follow();
    view.addEventListener("resize", follow);
    view.addEventListener("scroll", follow);
    return () => {
      view.removeEventListener("resize", follow);
      view.removeEventListener("scroll", follow);
    };
  });

  const OPEN_MS = 280;
  const CLOSE_MS = 200;
  const still = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /**
   * The panel's slide: up from a full height below on opening, back down on closing. On closing it
   * starts wherever a pull left it, so a sheet pulled half way does not jump back before it goes.
   * The transitions are `global` because callers open and close the sheet with their own `{#if}`.
   *
   * @param {HTMLElement} _node
   * @param {{ opening: boolean }} params
   */
  function slide(_node, { opening }) {
    const from = opening ? 0 : dragY;
    return {
      duration: still ? 0 : opening ? OPEN_MS : CLOSE_MS,
      easing: opening ? cubicOut : cubicIn,
      css: (/** @type {number} */ _t, /** @type {number} */ u) =>
        `transform: translateY(calc(${from}px + ${u * 100}%))`,
    };
  }

  // Lock the page behind for as long as the sheet is up, and keep focus with the sheet: into the
  // panel as it opens, unless the caller has already put it on a field, and back to whatever
  // opened it as it closes, so a keyboard or VoiceOver is not left at the top of the page. Focus
  // is only given back if nothing else has taken it: a sheet that opens as this one closes keeps it.
  onMount(() => {
    const root = document.documentElement;
    if (locks++ === 0) {
      unlockedOverflow = root.style.overflow;
      root.style.overflow = "hidden";
    }
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const fromSheet = active?.closest('[role="dialog"]');
    const opener = fromSheet && openers.has(fromSheet) ? (openers.get(fromSheet) ?? null) : active;
    if (panel) openers.set(panel, opener);
    queueMicrotask(() => {
      if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    });
    return () => {
      if (--locks === 0) root.style.overflow = unlockedOverflow;
      const here = document.activeElement;
      const unclaimed = !here || here === document.body || panel?.contains(here);
      if (unclaimed && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  });

  // touchmove is registered by hand: Svelte attaches it passively, and a passive listener cannot
  // stop the scroll that a pull has to replace.
  $effect(() => {
    if (!panel) return;
    const el = panel;
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  });

  /** @param {TouchEvent} event */
  function onTouchStart(event) {
    const target = /** @type {Element} */ (event.target);
    start =
      event.touches.length === 1 && !target.closest("input, textarea, select, [contenteditable]")
        ? { y: event.touches[0].clientY, at: event.timeStamp }
        : null;
  }

  /** @param {TouchEvent} event */
  function onTouchMove(event) {
    if (!start || !panel) return;
    const dy = event.touches[0].clientY - start.y;
    if (!dragging) {
      // Decided on the first move: only a downward pull from the top is ours; anything else
      // belongs to the sheet's own scrolling, for the rest of this touch.
      if (dy <= 0 || panel.scrollTop > 0) {
        start = null;
        return;
      }
      dragging = true;
    }
    event.preventDefault();
    dragY = Math.max(0, dy);
  }

  /** @param {TouchEvent} event */
  function onTouchEnd(event) {
    if (!dragging || !start) {
      start = null;
      return;
    }
    const velocity = dragY / Math.max(1, event.timeStamp - start.at);
    const close = dragY > CLOSE_DISTANCE || velocity > CLOSE_VELOCITY;
    start = null;
    dragging = false;
    if (close) onClose();
    else dragY = 0;
  }
</script>

<!-- Backdrop. Its own button so dismissing by tapping away is reachable by keyboard too;
     touch-none so a drag on it cannot scroll the page. -->
<button
  type="button"
  class="bg-ink/40 fixed inset-0 z-50 touch-none backdrop-blur-sm"
  aria-label={t("close")}
  onclick={onClose}
  in:fade|global={{ duration: still ? 0 : OPEN_MS }}
  out:fade|global={{ duration: still ? 0 : CLOSE_MS }}>
</button>

<div
  bind:this={panel}
  ontouchstart={onTouchStart}
  ontouchend={onTouchEnd}
  ontouchcancel={onTouchEnd}
  tabindex="-1"
  class="bg-surface rounded-card fixed inset-x-0 z-50 overflow-y-auto overscroll-contain rounded-b-none shadow-lg focus:outline-none"
  style:bottom="{keyboard}px"
  style:max-height={visible ? `min(90dvh, calc(${visible}px - env(safe-area-inset-top) - 1rem))` : "90dvh"}
  style:padding-bottom={keyboard ? "1rem" : "calc(env(safe-area-inset-bottom) + 1rem)"}
  style:transform={dragY ? `translateY(${dragY}px)` : undefined}
  style:transition={dragging ? "none" : "transform 200ms ease-out"}
  role="dialog"
  aria-modal="true"
  aria-label={label}
  in:slide|global={{ opening: true }}
  out:slide|global={{ opening: false }}>
  <div class="app-shell pt-3">
    <!-- Grab handle: the affordance that says this sheet can be pulled down to close. -->
    <div class="bg-hairline mx-auto mb-4 h-1 w-10 rounded-full" aria-hidden="true"></div>
    {@render children()}
  </div>
</div>
